/**
 * The address-scoped invitation list: every invitation `useMyInvitations`
 * returns, one row per invitation. A `WITHDRAWN` row is kept in the list
 * (the plan's own recommendation: an invitee whose only pointer was the
 * email should not meet a bare empty list once it is withdrawn) but rendered
 * read-only, with no accept action.
 *
 * The accept button stays disabled until `useMe()` resolves: accepting
 * before the caller's own `users` row is confirmed to exist is refused
 * server-side (`api`'s `_resolve_user_id`), and disabling here turns that
 * into "not yet" rather than a round trip that comes back a refusal.
 *
 * One `useAcceptInvitation()` instance for the whole list, not one per row:
 * only one row can be mid-accept at a time, and `mutation.variables` is how
 * a row tells whether the last call was its own.
 */
import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import type { UseMutationResult } from '@tanstack/react-query';

import { getLocale } from '../../../i18n';
import { EmptyState, QueryStates } from '../../../chrome/state';
import type { ApiFailureError } from '../../../lib/http/errors';
import { Button } from '../../../ui/button';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemGroup,
  ItemSeparator,
  ItemTitle,
} from '../../../ui/item';
import { useAuthSession } from '../../auth/AuthProvider';
import { HOME_PATH } from '../../routes/paths';
import { useMe } from '../../session/session';
import { tt, type TenantMessageKey } from '../i18n';
import { ta } from './messages';
import type { AcceptRefusalStatus, AcceptResponse, MyInvitation } from './schemas';
import { SwitchAccountBanner } from './SwitchAccountBanner';
import { useAcceptInvitation, type AcceptInvitationInput } from './useAcceptInvitation';
import { useMyInvitations } from './useMyInvitations';

const REFUSAL_KEY: Record<AcceptRefusalStatus, TenantMessageKey> = {
  withdrawn: 'accept.refused.withdrawn',
  expired: 'accept.refused.expired',
  already_accepted: 'accept.refused.already_accepted',
  no_longer_valid: 'accept.refused.invalid',
};

/**
 * `expires_at` is an instant, not a wall-clock date, and rendering it in the
 * viewer's own zone can shift the calendar day it shows across a midnight
 * boundary in every zone but UTC (`[green-locally-is-a-claim-about-the-machine]`).
 * Pinned to UTC explicitly rather than left to the ambient zone, so the date a
 * reader sees does not depend on where the invitee or the CI runner happens
 * to be.
 */
function formatExpiry(iso: string): string {
  return new Intl.DateTimeFormat(getLocale(), { dateStyle: 'medium', timeZone: 'UTC' }).format(
    new Date(iso),
  );
}

type Acceptance = UseMutationResult<AcceptResponse, ApiFailureError, AcceptInvitationInput>;

function InvitationRow(props: {
  invitation: MyInvitation;
  first: boolean;
  canAct: boolean;
  acceptance: Acceptance;
}): ReactNode {
  const { invitation, acceptance } = props;
  const isThisRow =
    acceptance.variables !== undefined &&
    'invitationId' in acceptance.variables &&
    acceptance.variables.invitationId === invitation.id;
  const refusal =
    isThisRow && acceptance.data && acceptance.data.status !== 'accepted'
      ? acceptance.data.status
      : null;
  const opaque = isThisRow && acceptance.isError;

  return (
    <>
      {!props.first && <ItemSeparator />}
      <Item role="listitem">
        <ItemContent>
          <ItemTitle>{invitation.tenant_name}</ItemTitle>
          {invitation.state === 'WITHDRAWN' ? (
            <p className="text-body-sm text-muted-foreground">
              {tt('accept.invite.withdrawn_inline')}
            </p>
          ) : refusal !== null ? (
            <p role="alert" className="text-body-sm text-destructive">
              {tt(REFUSAL_KEY[refusal])}
            </p>
          ) : opaque ? (
            <p role="alert" className="text-body-sm text-destructive">
              {tt('accept.refused.opaque')}
            </p>
          ) : (
            <p className="text-body-sm text-muted-foreground">
              {ta('expires', { date: formatExpiry(invitation.expires_at) })}
            </p>
          )}
        </ItemContent>
        {invitation.state === 'PENDING' && (
          <ItemActions>
            {refusal === 'already_accepted' ? (
              <Button asChild size="sm" variant="outline">
                <Link to={HOME_PATH}>{tt('accept.refused.already_accepted_action')}</Link>
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                disabled={!props.canAct || (isThisRow && acceptance.isPending)}
                onClick={() => acceptance.mutate({ invitationId: invitation.id })}
              >
                {tt('accept.invite.accept_action')}
              </Button>
            )}
          </ItemActions>
        )}
      </Item>
    </>
  );
}

export function MyInvitations(): ReactNode {
  const query = useMyInvitations();
  const me = useMe();
  const { session, state } = useAuthSession();
  const acceptance = useAcceptInvitation();

  const email = state.user?.email ?? null;
  const canAct = me.status === 'success';

  return (
    <div className="flex flex-col gap-4">
      <SwitchAccountBanner email={email} onSwitch={() => void session.signOut()} />

      <QueryStates query={query} empty={<EmptyState title={tt('accept.empty_state')} />}>
        {(items) => (
          <ItemGroup aria-label={tt('accept.page_title')}>
            {items.map((invitation, index) => (
              <InvitationRow
                key={invitation.id}
                invitation={invitation}
                first={index === 0}
                canAct={canAct}
                acceptance={acceptance}
              />
            ))}
          </ItemGroup>
        )}
      </QueryStates>
    </div>
  );
}
